// AI assisted development
/**
 * React Query hooks for dashboard notifications.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAuthenticated } from '../services/auth';
import { notificationApi } from '../services/notificationApi';
import { useDocumentVisible } from './useDocumentVisibility';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
};

export function useNotifications() {
  const isForeground = useDocumentVisible();
  const enabled = isForeground && isAuthenticated();

  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: () => notificationApi.list(),
    enabled,
    refetchInterval: enabled ? 5_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificationApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useClearNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationApi.clearAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
